// client/src/pages/admin/ManageMusic.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api.js';
import Button from '../../components/ui/Button.js';
import ManageMusicAttribution from './ManageMusicAttribution.js';

// ——— Music Edit Modal ———————————————————————————————
function MusicModal({ music, attributions, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    name: music?.name || '',
    composer: music?.composer || '',
    description: music?.description || '',
    musictype: music?.musictype || 'audio',
    musiccode: music?.musiccode || '',
    musicaudiourl: music?.musicaudiourl || '',
    previewduration: music?.previewduration || 60,
    attribution: music?.attribution || '',
    attributionid: music?.attributionid || '',
    isactive: music?.isactive !== false,
  });
  const [error, setError] = useState('');

  const isEditing = !!music?.musicid;

  const handleSave = () => {
    setError('');
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    onSave({
      ...form,
      attributionid: form.attributionid ? parseInt(form.attributionid) : null,
      previewduration: parseInt(form.previewduration) || 60,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-royal-700/50">
          <h2 className="text-gold font-display text-xl">
            {isEditing ? 'Edit Music' : 'Add Music'}
          </h2>
          <button onClick={onClose} className="text-royal-200 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-4">
          {error && <p className="text-red-400 text-base">{error}</p>}

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Name *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Track name"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Composer</label>
            <input
              type="text"
              value={form.composer}
              onChange={(e) => setForm({ ...form, composer: e.target.value })}
              placeholder="Composer name"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description"
              rows={2}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors resize-y"
            />
          </div>

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Music Type</label>
            <select
              value={form.musictype}
              onChange={(e) => setForm({ ...form, musictype: e.target.value })}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
            >
              <option value="audio">Audio File (MP3)</option>
              <option value="code">Synthesized (Tone.js)</option>
            </select>
          </div>

          {form.musictype === 'audio' && (
            <div>
              <label className="block text-sm text-royal-200 mb-1.5">Audio URL</label>
              <input
                type="url"
                value={form.musicaudiourl}
                onChange={(e) => setForm({ ...form, musicaudiourl: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
              />
            </div>
          )}

          {form.musictype === 'code' && (
            <div>
              <label className="block text-sm text-royal-200 mb-1.5">Music Code (JSON)</label>
              <textarea
                value={form.musiccode}
                onChange={(e) => setForm({ ...form, musiccode: e.target.value })}
                placeholder='[{"note": "C4", "duration": 0.5, "time": 0, "type": "piano_high"}]'
                rows={4}
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors font-mono text-sm resize-y"
              />
            </div>
          )}

          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Preview Duration (seconds)</label>
            <input
              type="number"
              value={form.previewduration}
              onChange={(e) => setForm({ ...form, previewduration: e.target.value })}
              min={10}
              max={300}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
            />
          </div>

          {/* License Attribution Dropdown */}
          <div>
            <label className="block text-sm text-royal-200 mb-1.5">License Attribution</label>
            <select
              value={form.attributionid || ''}
              onChange={(e) => setForm({ ...form, attributionid: e.target.value })}
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
            >
              <option value="">Select license...</option>
              {attributions.map((attr) => (
                <option key={attr.attributionid} value={attr.attributionid}>
                  {attr.name}
                </option>
              ))}
            </select>
            <p className="text-royal-200 text-sm mt-1">
              Select the license for this music track
            </p>
          </div>

          {/* Legacy Attribution Text */}
          <div>
            <label className="block text-sm text-royal-200 mb-1.5">Attribution Notes (Legacy)</label>
            <input
              type="text"
              value={form.attribution}
              onChange={(e) => setForm({ ...form, attribution: e.target.value })}
              placeholder="Additional attribution notes..."
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
            />
            <p className="text-royal-200 text-sm mt-1">
              Optional extra notes (prefer using the dropdown above)
            </p>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="isactive"
              checked={form.isactive}
              onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
              className="w-4 h-4 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold/50"
            />
            <label htmlFor="isactive" className="text-royal-200 text-base">Active (visible in templates)</label>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 pt-4 border-t border-royal-700/50">
          <button onClick={onClose} className="px-4 py-2.5 text-royal-200 text-base hover:text-white transition-colors">
            Cancel
          </button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Music'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ——— Music List Tab ———————————————————————————————————
function MusicListTab({ music, attributions, onEdit, onDelete, deleting }) {
  return (
    <div className="space-y-2">
      {music.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-royal-200 text-base">No music tracks yet</p>
        </div>
      ) : (
        music.map((m) => (
          <div
            key={m.musicid}
            className={`bg-royal-800/40 border rounded-xl p-4 flex items-start gap-4 ${
              m.isactive ? 'border-royal-700/50' : 'border-red-600/30 opacity-60'
            }`}
          >
            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
              <span className="text-xl">{m.musictype === 'audio' ? '🎵' : '🎹'}</span>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-white font-medium text-base truncate">{m.name}</h3>
                {!m.isactive && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/50 text-red-400">
                    Inactive
                  </span>
                )}
                {m.attributionname && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-royal-700 text-royal-200">
                    {m.attributionname}
                  </span>
                )}
              </div>
              {m.composer && (
                <p className="text-royal-200 text-sm">by {m.composer}</p>
              )}
              {m.description && (
                <p className="text-royal-200 text-sm mt-1 line-clamp-1">{m.description}</p>
              )}
              <p className="text-royal-200 text-sm mt-1">
                {m.musictype === 'audio' ? 'Audio file' : 'Synthesized'} · {m.previewduration}s
              </p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => onEdit(m)}
                className="p-2 text-royal-200 hover:text-gold rounded-lg hover:bg-royal-700/50 transition-colors"
                title="Edit"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                onClick={() => onDelete(m)}
                disabled={deleting === m.musicid}
                className="p-2 text-royal-200 hover:text-red-400 rounded-lg hover:bg-royal-700/50 transition-colors disabled:opacity-50"
                title="Delete"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ——— Main Component ———————————————————————————————————
export default function ManageMusic() {
  const [activeTab, setActiveTab] = useState('music'); // 'music' or 'attribution'
  const [music, setMusic] = useState([]);
  const [attributions, setAttributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMusic, setEditingMusic] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [musicData, attrData] = await Promise.all([
        api.get('/admin/music'),
        api.get('/admin/music-attribution'),
      ]);
      setMusic(musicData.music || []);
      setAttributions(attrData.attributions || []);
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Failed to load data');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveMusic = async (form) => {
    setSaving(true);
    setError('');
    try {
      if (editingMusic?.musicid) {
        await api.put(`/admin/music/${editingMusic.musicid}`, form);
      } else {
        await api.post('/admin/music', form);
      }
      setEditingMusic(null);
      fetchData();
    } catch (err) {
      setError(err.data?.error || 'Failed to save music');
    }
    setSaving(false);
  };

  const handleDeleteMusic = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? This cannot be undone.`)) return;

    setDeleting(m.musicid);
    setError('');
    try {
      await api.del(`/admin/music/${m.musicid}`);
      fetchData();
    } catch (err) {
      setError(err.data?.error || 'Failed to delete music');
    }
    setDeleting(null);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display text-2xl text-gold">Music Manager</h1>
          <p className="text-royal-200 text-base">
            {activeTab === 'music' 
              ? `${music.length} track${music.length !== 1 ? 's' : ''}` 
              : 'Manage license types'}
          </p>
        </div>
        {activeTab === 'music' && (
          <Button onClick={() => setEditingMusic({})}>+ Add Music</Button>
        )}
      </div>

      {/* Tab Bar */}
      <div className="flex gap-2 mb-6 border-b border-royal-700/50">
        <button
          onClick={() => setActiveTab('music')}
          className={`px-4 py-2.5 text-base font-medium transition-colors relative ${
            activeTab === 'music' ? 'text-gold' : 'text-royal-200 hover:text-white'
          }`}
        >
          🎵 Music
          {activeTab === 'music' && (
            <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-gold rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('attribution')}
          className={`px-4 py-2.5 text-base font-medium transition-colors relative ${
            activeTab === 'attribution' ? 'text-gold' : 'text-royal-200 hover:text-white'
          }`}
        >
          📜 Attribution
          {activeTab === 'attribution' && (
            <div className="absolute bottom-0 left-2 right-2 h-0.5 bg-gold rounded-full" />
          )}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-base">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
        </div>
      ) : activeTab === 'music' ? (
        <MusicListTab
          music={music}
          attributions={attributions}
          onEdit={setEditingMusic}
          onDelete={handleDeleteMusic}
          deleting={deleting}
        />
      ) : (
        <ManageMusicAttribution />
      )}

      {/* Music Edit Modal */}
      {editingMusic !== null && (
        <MusicModal
          music={editingMusic}
          attributions={attributions}
          onSave={handleSaveMusic}
          onClose={() => setEditingMusic(null)}
          saving={saving}
        />
      )}
    </div>
  );
}